import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Row, Col } from 'antd'
import { useFormContext } from 'react-hook-form'

import Button from 'components/Button'
import CancellationRules from 'components/CancellationRules'
import FormattedMessage from 'components/FormattedMessage'
import FormSection from 'components/FormSection'
import FormItem from 'components/Form/Item'
import Checkbox from 'components/Form/Checkbox'
import Tooltip from 'components/Tooltip'
import Icon from 'components/Icon'

import EventDetails from 'components/ContactRequestFormComponents/EventDetails'
import ContactFields from 'components/ContactRequestFormComponents/ContactFields'
import ProviderTermsAndConditionsCheckbox
  from 'components/ContactRequestFormComponents/ProviderTermsAndConditionsCheckbox'

import './index.less'

const ProviderContactForm = (props) => {
  const {
    currentUser,
    ctaHintTranslationKey,
    headlineTranslationKey,
    provider,
    submitForm,
    isSubmitting,
    disableSubmit,
    ctaTranslationKey = 'contactRequestForm.withExtendedRequestForm.cta',
    initialData = {},
  } = props

  const { handleSubmit } = useFormContext()
  const hasCancellationRules = provider.cancellationRules?.length > 0

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      {headlineTranslationKey && (
        <FormSection className="contact-request__header">
          <h1 className="contact-request__header__headline">
            <FormattedMessage id={headlineTranslationKey} />
          </h1>
        </FormSection>
      )}

      <FormSection label={<FormattedMessage id="contactRequest.sections.eventDetails" />}>
        <EventDetails provider={provider} initialData={initialData} />
      </FormSection>

      {!currentUser && (
        <FormSection label={<FormattedMessage id="contactRequest.sections.contactData" />}>
          <Row>
            <Col>
              <ContactFields
                asSingleCol={true}
                autoFocusName={false}
                eventCategories={provider.eventCategories}
                initialData={initialData}
              />
            </Col>
          </Row>
        </FormSection>
      )}

      <FormSection>
        <Row>
          <Col>
            {!currentUser && (
              <>
                <div className="contact-request__form__check-item contact-request__form__terms-conditions">
                  <FormItem className="terms-conditions__consent-wrapper">
                    <Checkbox
                      name="consent"
                      rules={{ required: <FormattedMessage id="errors.empty" /> }}
                      className="contact-request__form__terms-conditions__consent"
                    >
                      <FormattedMessage id="contactRequestForm.consent" tagName="span" html />
                    </Checkbox>
                  </FormItem>
                </div>

                <div className="contact-request__form__check-item">
                  <FormItem>
                    <Checkbox name="newsletter">
                      <span className="contact-request__form__newsletter__consent">
                        <FormattedMessage id="contactRequestForm.newsletter" />
                      </span>
                      <Tooltip
                        placement="top"
                        content={
                          <FormattedMessage
                            id="contactRequestForm.newsletter.tooltip"
                            values={{
                              link: (chunks) => (
                                <a
                                  href="https://about.eventinc.de/privacy-policy/"
                                  target="_blank"
                                  className="link"
                                >
                                  {chunks}
                                </a>
                              ),
                            }}
                          />
                        }
                      >
                        <Icon name="info-circle-bordered" />
                      </Tooltip>
                    </Checkbox>
                  </FormItem>
                </div>
              </>
            )}

            {provider.type === 'EventRoom' && (
              <>
                <div
                  className={classNames(
                    'contact-request__form__check-item',
                    'contact-request__form__provider-terms-conditions',
                    {
                      'contact-request__form__provider-terms-conditions--no-margin': hasCancellationRules,
                    },
                  )}
                >
                  <ProviderTermsAndConditionsCheckbox
                    provider={provider}
                    initialData={initialData}
                  />
                </div>

                {hasCancellationRules && (
                  <CancellationRules
                    rules={provider.cancellationRules}
                    className="contact-request__form__cancellation-rules"
                  />
                )}
              </>
            )}
          </Col>
        </Row>

        <Row>
          <Col>
            <Button
              translationKey={ctaTranslationKey}
              type="primary"
              size="large"
              htmlType="submit"
              className="contact-request__form__submit ant-btn--with-border-radius-small"
              disabled={isSubmitting || disableSubmit}
            />

            {ctaHintTranslationKey && (
              <div className="contact-request__form__cta-hint">
                <FormattedMessage id={ctaHintTranslationKey} />
              </div>
            )}
          </Col>
        </Row>
      </FormSection>
    </form>
  )
}

ProviderContactForm.propTypes = {
  currentUser: PropTypes.object,
  ctaHintTranslationKey: PropTypes.string,
  ctaTranslationKey: PropTypes.string,
  headlineTranslationKey: PropTypes.string,
  initialData: PropTypes.object,
  provider: PropTypes.shape({
    type: PropTypes.string,
    cancellationRules: PropTypes.arrayOf(PropTypes.object),
    isDirectPayable: PropTypes.bool,
    eventCategories: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  submitForm: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  disableSubmit: PropTypes.bool,
}

export default ProviderContactForm
